/**
 * useBacktestDeeplink
 *
 * On mount, reads `?backtest=<id>` from the URL query string. If present and
 * non-empty, fetches that backtest and navigates to the 'backtesting' page so
 * the result is visible. Works for both single-asset and aggregation runs.
 *
 * This handles deep-links generated by the hosted MCP server, e.g.:
 *   https://sharpelab.xyz/?backtest=mcp-1779341184195
 *
 * After loading, the URL is normalised to the path-based format
 * (/backtesting/<id>) by the existing useUrlSync hook reacting to the
 * selectedBacktestId store update — no extra replaceState needed here.
 *
 * Edge-cases:
 * - Empty ?backtest= param → ignored (no fetch)
 * - 404 / network error → console.warn, no crash, URL param left intact
 * - Runs once on mount only (empty dependency array)
 */

import { useEffect } from 'react';
import { useLoadBacktest } from './useBacktest';
import { usePaperTradingStore } from '../stores/paperTradingStore';

export function useBacktestDeeplink(): void {
  const { loadBacktest } = useLoadBacktest();
  const setActivePage = usePaperTradingStore((s) => s.setActivePage);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const backtestId = params.get('backtest');

    // Ignore absent or empty param
    if (!backtestId) return;

    void (async () => {
      try {
        const result = await loadBacktest(backtestId);

        if (!result) {
          console.warn(`[useBacktestDeeplink] Backtest "${backtestId}" not found (loadBacktest returned null)`);
          return;
        }

        // Navigate to the backtesting page so the result is visible.
        // Both single-asset and aggregation runs display on the backtesting
        // page — the AggregationsPanel is a sidebar section within that page,
        // not a separate top-level page.
        setActivePage('backtesting');
      } catch (err) {
        console.warn(
          `[useBacktestDeeplink] Failed to load backtest "${backtestId}":`,
          err instanceof Error ? err.message : err,
        );
      }
    })();

    // Run once on mount only
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
